"use client";
import type { FC, JSX, ReactElement } from "react";
import { isValidElement, memo, useEffect, useId, useRef, useState } from "react";
import { A11y, Autoplay, Keyboard, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/autoplay";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Swiper, SwiperSlide } from "swiper/react";
import { CustomProgressBar } from "../Progress";
import type { SwiperProps } from "swiper/react";
import type { Swiper as SwiperClass } from "swiper/types";
import { styles } from "../CarouselStyles";
import { Box } from "../../../layout/box/Box";
import type { BoxProps } from "../../../layout/box/Box";
import type { FlexProps } from "../../../layout/flex/Flex";
import type { SwipeCarouselVideoPlayback } from "./SwipeCarouselNavigation";
import { SwipeCarouselNavigation } from "./SwipeCarouselNavigation";
import { SwipeCarouselPagination } from "./SwipeCarouselPagination";

const DEFAULT_TIMER = false;

type SwipeCarouselIndicator = "pagination" | "progress";

type SwipeCarouselNavigationConfig = {
    containerSx?: FlexProps["sx"];
    previousSlideTitle?: string;
    nextSlideTitle?: string;
    buttonNavSx?: {
        rightButton?: BoxProps["sx"];
        leftButton?: BoxProps["sx"];
    };
};

type SwipeCarouselPlaybackConfig = {
    hasTimer?: boolean;
    videoPlayback?: SwipeCarouselVideoPlayback;
};
export type SwipeCarouselProps = {
    children: ReactElement | ReactElement[];
    selectedSlideIndex?: number;
    onSlideIndexChange?: (index: number) => void;
    navigation?: SwipeCarouselNavigationConfig;
    playback?: SwipeCarouselPlaybackConfig;
    sx?: BoxProps["sx"];
    indicator: SwipeCarouselIndicator;
    hasLoop?: boolean;
};

export const SwipeCarousel: FC<SwipeCarouselProps> = memo(
    ({
        children,
        sx,
        navigation,
        selectedSlideIndex = 0,
        onSlideIndexChange,
        playback,
        indicator,
        hasLoop = true,
    }): JSX.Element => {
        const {
            containerSx,
            nextSlideTitle = "",
            previousSlideTitle = "",
            buttonNavSx,
        } = navigation ?? {};
        const { hasTimer = DEFAULT_TIMER, videoPlayback } = playback ?? {};

        const [activeSlideState, setActiveSlideState] = useState(selectedSlideIndex ?? 0);
        const arrayChildren = (
            Array.isArray(children) ? children.flat(Infinity) : [children]
        ).filter((child): child is ReactElement => isValidElement(child));
        const slides = arrayChildren.map((child, index) => ({
            child,
            slideKey: child.key !== null ? `key:${child.key}` : `index:${index}`,
        }));

        const swiperRef = useRef<SwiperClass>(null);
        const navigationId = useId().replace(/:/g, "");

        const timerClass = hasTimer
            ? `swiper-pagination-timer-${navigationId}`
            : `swiper-pagination-${navigationId}`;
        const previousButtonClassName = `swiper-button-prev-${navigationId}`;
        const nextButtonClassName = `swiper-button-next-${navigationId}`;

        // Create a key that changes when the number of slides changes
        // This forces React to remount the Swiper component
        const swiperKey = `swiper-${navigationId}-${arrayChildren.length}`;

        // Only enable loop if there are more than 1 slides
        const shouldLoop = hasLoop && arrayChildren.length > 1;

        const swiperOptions: SwiperProps = {
            tag: "div",
            speed: 450,
            slidesPerView: 1,
            spaceBetween: 24,
            initialSlide: selectedSlideIndex ?? 0,

            // Performance and UX
            watchSlidesProgress: true,
            resistance: true,
            resistanceRatio: 0,
            autoHeight: false,
            cssMode: !shouldLoop && !hasTimer,
            loop: shouldLoop,

            // Resize and observer optimizations
            updateOnWindowResize: false, // Batch resize handling
            observer: false,
            observeParents: false, // Disable mutation observers

            // Navigation controls
            navigation: {
                nextEl: `.${nextButtonClassName}`,
                prevEl: `.${previousButtonClassName}`,
                disabledClass: "swiper-button-disabled",
            },

            // Pagination (dots)
            pagination: {
                el: `.${timerClass}`,
                type: "bullets",
                clickable: true,
            },

            // Accessibility
            a11y: {
                prevSlideMessage: "Previous slide",
                nextSlideMessage: "Next slide",
            },

            // Keyboard control
            keyboard: {
                enabled: true,
                onlyInViewport: false,
            },

            // Core modules
            modules: [Navigation, Pagination, A11y, Autoplay, Keyboard],

            // Inline style
            style: {
                position: "relative",
                overflow: "clip",
            },
        };

        const swiperNavigationContainer = {
            ...styles.swiperNavigationContainer,
            ...containerSx,
        };

        const totalSlides = arrayChildren.length;
        const currentIndex =
            (Number.isFinite(activeSlideState) ? activeSlideState : 0) ??
            swiperRef?.current?.realIndex ??
            0;
        const progressValue = totalSlides > 0 ? ((currentIndex + 1) / totalSlides) * 100 : 0;

        // Handle externally controlled slide index changes
        useEffect(() => {
            if (swiperRef.current && selectedSlideIndex !== undefined) {
                const swiper = swiperRef.current;
                if (swiper.realIndex !== selectedSlideIndex) {
                    if (shouldLoop) {
                        swiper.slideToLoop(selectedSlideIndex);
                    } else {
                        swiper.slideTo(selectedSlideIndex);
                    }
                }
                setActiveSlideState(selectedSlideIndex);
            }
        }, [selectedSlideIndex, shouldLoop]);

        const delay = (
            arrayChildren[activeSlideState]?.props as
                | {
                      module?: {
                          fields?: { backgroundVideo?: { items?: { duration?: number }[] } };
                      };
                  }
                | undefined
        )?.module?.fields?.backgroundVideo?.items?.[0]?.duration;

        return (
            <Box sx={{ ...styles.swiperContainer, ...sx }}>
                <Swiper
                    key={swiperKey}
                    onSlideChange={(swiper: SwiperClass) => {
                        const realIndex = swiper.realIndex;
                        onSlideIndexChange?.(realIndex);
                        setActiveSlideState(realIndex);
                    }}
                    onSwiper={(swiper: SwiperClass) => ((swiperRef.current as any) = swiper)}
                    {...swiperOptions}
                    scrollbar={{ draggable: true }}
                    autoplay={
                        hasTimer
                            ? {
                                  delay: delay ? delay * 1000 : 5000,
                                  disableOnInteraction: false,
                                  pauseOnMouseEnter: true,
                              }
                            : false
                    }
                >
                    {slides.map(({ child, slideKey }) => (
                        <SwiperSlide key={slideKey}>{child}</SwiperSlide>
                    ))}
                </Swiper>

                <SwipeCarouselNavigation
                    containerStyle={swiperNavigationContainer}
                    nextSlideTitle={nextSlideTitle}
                    previousSlideTitle={previousSlideTitle}
                    navsx={buttonNavSx}
                    swiperRef={swiperRef}
                    videoPlayback={videoPlayback}
                    previousButtonClassName={previousButtonClassName}
                    nextButtonClassName={nextButtonClassName}
                />

                {indicator === "pagination" && (
                    <SwipeCarouselPagination
                        numOfSlides={arrayChildren.length}
                        activeIndex={activeSlideState}
                    />
                )}

                {indicator === "progress" && <CustomProgressBar value={progressValue} />}
            </Box>
        );
    }
);

SwipeCarousel.displayName = "SwipeCarousel";
